import { Dialog, DialogConfig } from '@angular/cdk/dialog';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StayAliveDialogComponent } from '@shared/stay-alive-dialog/stay-alive-dialog.component';
import { CookieService } from 'ngx-cookie-service';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from './../../environments/environment';
import { AuthService } from './authentication/auth.service';
import { isInspectorUser } from './authentication/auth.utility';
import { AlertService, LoaderService } from './services';

/** Set to `true` when the caller shows its own error feedback, to suppress the generic toasts. */
export const SKIP_SERVER_ALERTS = new HttpContextToken<boolean>(() => false);
const INSPECTOR_PORTAL_API = /^(?:api\/)?inspector\//;
/**
 * A single report/template resource (view, rename, delete, acl, ...). The report service answers
 * 404 (not found) or 405 (no access) here - not 403 - and the reports UI reads the status itself
 * to show its own message, so these aren't a sign that the caller's session is invalid.
 */
const REPORT_RESOURCE_API = /report\/dynamic\/(?:reports|templates)\/\d+(?:[/?]|$)/;

@Injectable()
export class HttpIntercept implements HttpInterceptor {
  private stayAliveDialogOpen = false;

  constructor(
    public dialog: Dialog,
    private authService: AuthService,
    private router: Router,
    private cookieService: CookieService,
    private loaderService: LoaderService,
    private alertService: AlertService
  ) {
    // Subscribe to the auth service's dialog trigger
    this.authService.shouldShowDialog$.subscribe(() => {
      if (!this.stayAliveDialogOpen) {
        this.openStayAliveDialog();
      }
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const excludePath =
      /(?:login|resetpassword|applications\/broker-portal|attachments\/broker-portal|borrower-portal)+/g;
    const includePath = /(?:login-as|logout-as|applications\/broker-portal\/update)+/g;
    const pollingPath = /(?:updates-count|exports)+/g;
    const overrideAuthToken = request.headers.get('Authorization-Override');
    if (overrideAuthToken) {
      request = request.clone({
        headers: request.headers
          .set('Authorization', overrideAuthToken)
          .delete('Authorization-Override'),
      });
      return next.handle(request);
    }
    if (request.url.search(excludePath) === -1 || request.url.search(includePath) > -1) {
      if (request.url.search(pollingPath) === -1) {
        this.authService.extendCookie();
      } else {
        const user = this.cookieService.get('currentUser');
        try {
          const parsedUser = JSON.parse(user);
          const expiryTimeProp = parsedUser.expiryTime;
          const expiryTime = new Date(expiryTimeProp).getTime();
          const currentTime = new Date().getTime();
          const cookieLifeRemaining = (expiryTime - currentTime) / 1000 / 60;

          if (cookieLifeRemaining < 2) {
            this.openStayAliveDialog();
          }
        } catch (error) {
          console.error('Error parsing user cookie:', error);
          // Invalid cookie format or missing cookie - logoutUser
          this.handleLogout();
        }
      }

      if (!this.excludedUrls(request)) {
        this.loaderService.startLoader();
      }

      const userAuthToken = localStorage.getItem('token')
        ? 'Bearer ' + localStorage.getItem('token')
        : '';
      const authorizationHeader = userAuthToken;
      if (authorizationHeader) {
        request = request.clone({
          headers: request.headers
            .set('Authorization', authorizationHeader)
            .delete('Authorization-Override'),
        });
      }
    }

    request = request.clone({
      url: this.updateUrl(request.url),
    });

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (
            this.ensureAllowedServerAlertsRequestMethods(request) &&
            !this.excludedUrls(request) &&
            !this.router.url.includes('/v2/applications/add')
          ) {
            this.alertService.success(null, 'Close');
          }
          this.returnResponse(request);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const skipAlerts = request.context.get(SKIP_SERVER_ALERTS);
        if (!(error.error instanceof ErrorEvent)) {
          if (
            !skipAlerts &&
            this.ensureAllowedServerAlertsRequestMethods(request) &&
            !this.excludedUrls(request) &&
            !this.isConfirmationRequired(error)
          ) {
            //When deleting a payment and a validation exception occurs, display the return message to the user
            if (request.url.startsWith('api/fundings') && request.method === 'DELETE') {
              this.alertService.error(error.error[0], 'Close', 6000);
            } else if (request.url.includes('api/applications/broker-portal')) {
              this.alertService.error(
                'Broker portal update is required. Please refresh the page.',
                'Close',
                6000
              );
            } else {
              this.alertService.error(null, 'Close');
            }
          }
        }
        if (request.url.search(/borrower-portal/g) !== -1) {
          this.returnResponse();
          return throwError(() => error);
        } else if (request.url.search(INSPECTOR_PORTAL_API) !== -1) {
          // The portal shows its own "not found" for 403/404; only an expired session logs out.
          if (error.status === 401) {
            this.catchErrors(error);
          }
          this.returnResponse();
          return throwError(() => error);
        } else if (request.url.search(/broker-portal/g) !== -1) {
          this.returnResponse();
          return next.handle(request);
        } else if (request.url.search(REPORT_RESOURCE_API) !== -1) {
          // Only an expired session (401) should log the user out; a 404/405 here is just this
          // report/template's own answer (not found / no access). Rethrow the full response (not
          // just `error.error`) so callers can read `error.status` to tell the two apart.
          if (error.status === 401) {
            this.catchErrors(error, skipAlerts);
          }
          this.returnResponse();
          return throwError(() => error);
        } else {
          this.catchErrors(error, skipAlerts);
          this.returnResponse();
          return throwError(() => error.error);
        }
      })
    );
  }

  private catchErrors(error: HttpErrorResponse, skipAlerts = false) {
    const applicationBaseUrlPattern = /\/api\/applications\/\d+$/;
    // Inspectors have no lender access - a 403 from a lender call must not end their session.
    if (error.status === 403 && isInspectorUser(this.authService.userRoles)) {
      return;
    }
    if ([401, 403].indexOf(error.status) !== -1) {
      this.authService.logout().subscribe({
        next: (r: any) => {
          this.killSession();
        },
        error: reason => {
          this.killSession();
        },
      });
    } else if (skipAlerts) {
      return;
    } else if ([504].indexOf(error.status) !== -1) {
      this.alertService.error('Server timed out. Reload and try again.', 'Close', 8000);
    } else if ([400].indexOf(error.status) !== -1) {
      // Login MFA auth error, do not display toast message
      if (error.error.token) {
        return;
      }
      // The funding dialogs show why a construction draw's payoff check failed on the deal itself.
      if (error.url?.includes('/construction-draw/refinance-change')) {
        return;
      }
      if (error.error instanceof Blob) {
        error.error.text().then(errorText => {
          let parsedError: any = errorText;
          try {
            parsedError = JSON.parse(errorText);
          } catch {}
          this.alertService.error(
            parsedError.error || parsedError.message || errorText,
            'Close',
            8000
          );
        });
      }
      // The funding dialogs show why a construction draw's payoff check failed on the deal itself.
      if (error.url?.includes('/construction-draw/refinance-change')) {
        return;
      }
      if (Array.isArray(error.error)) {
        this.alertService.error(error.error[0], 'Close', 15000);
      } else {
        this.alertService.error(this._resolveErrorMessage(error.error), 'Close', 8000);
      }
    } else if ([404].indexOf(error.status) !== -1 && applicationBaseUrlPattern.test(error.url)) {
      this.alertService.error(error.error, 'Close', 15000);
      setTimeout(() => {
        this.router.navigate(['/applications']);
      }, 3000);
    }
  }

  private _resolveErrorMessage(body: any): string | null {
    if (!body) {
      return null;
    }
    if (typeof body === 'string') {
      return body;
    }
    if (Array.isArray(body.messages) && body.messages.length) {
      const text = body.messages
        .map((message: any) => message?.text)
        .filter(Boolean)
        .join(' ');
      if (text) {
        return text;
      }
    }
    if (typeof body.message === 'string') {
      return body.message;
    }
    return null;
  }

  /** A 409 with a `type` asks the caller to confirm; the caller shows it, not a toast. */
  private isConfirmationRequired(error: HttpErrorResponse): boolean {
    return error.status === 409 && typeof error.error?.type === 'string';
  }

  private ensureAllowedServerAlertsRequestMethods(request): boolean {
    const allowedMethods: Array<string> = ['PUT', 'DELETE', 'POST', 'PATCH'];
    return allowedMethods.includes(request.method);
  }

  private excludedUrls(request): boolean {
    const excludePaths =
      /(?:login|logout|login-as|resetpassword|attachments|calculator|calendar|search|exports|notes\/mark-read|borrower-portal)+/g;
    return request.url.search(excludePaths) > -1 || request.url.search(INSPECTOR_PORTAL_API) > -1;
  }

  updateUrl(req: string) {
    if (
      (environment.production === true ||
        environment.staging === true ||
        environment.development === true) &&
      (req.startsWith('clear/') ||
        req.startsWith('calendar/') ||
        req.startsWith('moneythumb/') ||
        req.startsWith('report/') ||
        req.startsWith('crs/') ||
        req.startsWith('scorecard/') ||
        req.startsWith('workflow/') ||
        req.startsWith('plaid/'))
    ) {
      return environment.servicesUrl.slice(0, -4) + req;
    }
    if (req.startsWith('https')) {
      return req;
    }
    return environment.servicesUrl + req;
  }

  killSession() {
    this.authService.killSession();
    this.router.navigate(['/login']);
  }

  returnResponse(request?: any) {
    if (request) {
      if (!this.excludedUrls(request)) {
        this.loaderService.stopLoader();
      }
    } else {
      this.loaderService.stopLoader();
    }
  }

  openStayAliveDialog() {
    if (this.stayAliveDialogOpen) {
      return;
    }

    this.stayAliveDialogOpen = true;

    const dialogConfig = new DialogConfig();
    dialogConfig.width = '700px';
    dialogConfig.maxWidth = '700px';
    dialogConfig.disableClose = true;

    const dialogRef = this.dialog.open(StayAliveDialogComponent, dialogConfig);
    dialogRef.closed.subscribe((result: any) => {
      this.stayAliveDialogOpen = false;

      if (result === 'extend') {
        this.authService.extendCookie();
      } else {
        this.handleLogout();
      }
    });
  }

  handleLogout() {
    this.authService.logout().subscribe((r: any) => {
      document.cookie.split(';').forEach(function (c) {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });
      localStorage.removeItem('token');
      if (localStorage.getItem('selectedWhiteLabels')) {
        localStorage.removeItem('selectedWhiteLabels');
      }
      this.router.navigate(['/login']);
    });
  }
}
